import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { setSeo } from "@/lib/events";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";

const THIS_YEAR = new Date().getFullYear();

export default function Sports() {
  const [params, setParams] = useSearchParams();
  const DEFAULT_YEAR = 2000;
  const year = Math.min(THIS_YEAR, Number(params.get("year")) || DEFAULT_YEAR);
  const comp = params.get("competition") || "";
  const [data, setData] = useState(null);
  const [comps, setComps] = useState([]);
  const [compData, setCompData] = useState(null);

  useEffect(() => { api.get("/sports/competitions").then((r) => setComps(r.data.competitions)).catch(() => {}); }, []);
  useEffect(() => { setSeo({ title: `Sports Almanac ${year} — World Series, Super Bowl, Wimbledon, Olympics and every champion by year | OddlyTrue`, description: `Who won the World Series, Super Bowl, Stanley Cup, NBA Finals, Wimbledon, The Masters, Tour de France and the World Cup in ${year}? Year-by-year champions and finals dates, verified against the record books.`, canonical: `https://oddlytrue.co/sports${year !== DEFAULT_YEAR ? `?year=${year}` : ""}` }); }, [year, DEFAULT_YEAR]);
  useEffect(() => {
    if (comp) { setCompData(null); api.get(`/sports/competition/${encodeURIComponent(comp)}`).then((r) => setCompData(r.data)).catch(() => setCompData({ items: [] })); return; }
    setData(null);
    api.get(`/sports/year/${year}`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [year, comp]);

  const go = (y) => setParams({ year: Math.max(1877, Math.min(THIS_YEAR, y)) }, { replace: true });

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="sports-page">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-gold" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Sporting almanac</p>
        </div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-ink leading-tight">{comp ? comp : `The sporting year ${year}`}</h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">908 momentous results across 16 competitions — World Cups, Olympics, Wimbledon, the Masters, Super Bowl, World Series, Tour de France and more.</p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button type="button" data-testid="sports-prev" onClick={() => go(year - 1)} disabled={!!comp} className="w-10 h-10 rounded-full border border-[#e5e7eb] bg-white text-ink hover:border-gold disabled:opacity-30 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <input data-testid="sports-year-input" type="number" min="1877" max={THIS_YEAR} value={year} onChange={(e) => go(Number(e.target.value))} className="w-28 h-10 rounded-full border border-[#e5e7eb] bg-white text-center text-[16px] font-bold text-ink outline-none focus:border-gold" />
          <button type="button" data-testid="sports-next" onClick={() => go(year + 1)} disabled={!!comp || year >= THIS_YEAR} className="w-10 h-10 rounded-full border border-[#e5e7eb] bg-white text-ink hover:border-gold disabled:opacity-30 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          <select data-testid="sports-competition" value={comp} onChange={(e) => setParams(e.target.value ? { competition: e.target.value } : { year }, { replace: true })} className="ml-auto h-10 rounded-full border border-[#e5e7eb] bg-white px-4 text-sm text-ink outline-none focus:border-gold">
            <option value="">By year</option>
            {comps.map((c) => <option key={c.competition} value={c.competition}>{`${c.competition} (${c.first}–${c.last})`}</option>)}
          </select>
        </div>

        <AdSlot id="adsense-sports" slot="TOP" size="leaderboard" className="my-8" />

        {comp ? (
          !compData ? <p className="text-sm text-ink-muted">Loading…</p> : (
            <ul className="bg-white border border-[#e5e7eb] rounded-2xl px-4" data-testid="sports-competition-list">
              {compData.items.map((s) => (
                <li key={s.id} className="flex items-start gap-4 py-3 border-b border-[#f1f1f1] last:border-0">
                  <button type="button" onClick={() => setParams({ year: s.year }, { replace: true })} className="w-14 shrink-0 text-[16px] font-bold text-gold hover:underline text-left">{s.year}</button>
                  <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-ink">{s.title}</span><span className="block text-[13px] text-ink-muted">{s.detail}</span></span>
                </li>
              ))}
            </ul>
          )
        ) : !data ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : data.items.length === 0 ? (
          <p data-testid="sports-empty" className="text-sm text-ink-muted">No results recorded for {year}. Try another year.</p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sports-year-list">
            {data.items.map((s) => (
              <li key={s.id} data-testid={`sports-item-${s.id}`} className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                <button type="button" onClick={() => setParams({ competition: s.competition }, { replace: true })} className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold hover:underline">{s.competition}{s.season ? ` · ${s.season}` : ""}</button>
                <h2 className="mt-1 text-lg font-bold text-ink leading-snug">{s.title}</h2>
                <p className="mt-1 text-[13px] text-ink-muted">{s.detail}</p>
              </li>
            ))}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}
